import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { IntlProvider } from "react-intl";
import "ui.bambora/dist/ui.bambora.1.0.0.css";
import "./assets/styles.scss";
import storeCreator from "./store";
import { useConfigLoader } from "./hooks/useConfigLoader";
import { SscIamProvider } from "./login/ssciam/SscIamProvider";
import NavPane from "./common/navPane";
import { NavPaneSscIam } from "./common/navPaneSscIam";
import errors from "./errors";
import ImpersonationContainer from "./impersonation/containers/impersonationContainer";
import MerchantDetailViewContainer from "./impersonation/containers/merchantDetailViewContainer";
import {
  doubleAuthEnabled,
  hasAnyValidToken,
  LAST_USED_LOGIN_EMAIL,
  setSscIamTokens,
  ssciamIsEnabled,
} from "./common/authHelper";
import { Redirect } from "react-router-dom";
import { LoginContainer } from "./login/login";
import { SsciamAuthLabel } from "./common/authLabel/SsciamAuthLabel";
import { CognitoAuthLabel } from "./common/authLabel/CognitoAuthLabel";
import { PrivateRoute } from "./common/authLabel/PrivateRoute";
import "./index.scss";

const { LoggedOutMessage } = errors;
const store = storeCreator();
const doubleAuth = doubleAuthEnabled();
const enableSscIam = ssciamIsEnabled();

const LoginPage = ({ store }) => {
  const isValidToken = hasAnyValidToken();

  if (isValidToken) {
    return <Redirect to="/" />;
  }

  return <LoginContainer store={store} />;
};

const AppLayout = ({ store }) => {
  return (
    <div style={{ position: "relative" }}>
      {enableSscIam ? <NavPaneSscIam /> : <NavPane />}
      {doubleAuth && (
        <div style={{ position: "fixed", top: 70, right: 10, zIndex: 1000 }}>
          <SsciamAuthLabel />
          <CognitoAuthLabel />
        </div>
      )}

      <LoggedOutMessage errors={store.getState().get("errors")} />
      <Switch>
        <PrivateRoute exact path="/" component={ImpersonationContainer} />
        <PrivateRoute exact path="/merchant/:merchantId" component={MerchantDetailViewContainer} />
      </Switch>
    </div>
  );
};

const RootSscIam = () => {
  const onAuthenticationComplete = ({ idToken, idTokenParsed }) => {
    if (idToken) {
      setSscIamTokens(idToken, idTokenParsed.sub || "");
      const email = idTokenParsed?.email || idTokenParsed?.name || "";
      window.localStorage.setItem(LAST_USED_LOGIN_EMAIL, email);
      window.location.replace("/"); // cant use history here because comp is not inside router
    }
  };

  const state = store.getState();
  const { oidcProvider, sscIamClientId } = state.getIn(["config", "endpoints"]);

  return (
    <Provider store={store}>
      <IntlProvider locale="en">
        <SscIamProvider
          oidcProvider={oidcProvider}
          clientId={sscIamClientId}
          onAuthenticationComplete={onAuthenticationComplete}
        >
          <BrowserRouter>
            <Switch>
              <Route exact path="/login" render={() => <LoginPage store={store} />} />
              <Route path="/" render={() => <AppLayout store={store} />} />
            </Switch>
          </BrowserRouter>
        </SscIamProvider>
      </IntlProvider>
    </Provider>
  );
};

const RootWithoutSscIam = () => {
  return (
    <Provider store={store}>
      <IntlProvider locale="en">
        <BrowserRouter>
          <Switch>
            <Route exact path="/login" render={() => <LoginPage store={store} />} />
            <Route path="/" render={() => <AppLayout store={store} />} />
          </Switch>
        </BrowserRouter>
      </IntlProvider>
    </Provider>
  );
};

const Root = () => {
  const configLoaded = useConfigLoader(store);

  if (!configLoaded) {
    return null;
  }

  return enableSscIam ? <RootSscIam /> : <RootWithoutSscIam />;
};

const rootElement = document.createElement("div");
document.body.appendChild(rootElement);
const root = createRoot(rootElement);
root.render(<Root />);
